/**
 * Modular Decorative Frame Generator
 *
 * This module generates SVG elements for decorative frames with parametric dimensions.
 * A single corner decoration is defined and then rotated/mirrored to create all four corners.
 * Frame lines and rivets are generated based on the frame dimensions.
 *
 * Usage:
 *   generateDecorativeFrame(transform, { x: 12.8, y: 195.7, width: 186.4, height: 89.4 })
 */

// =============================================================================
// CONSTANTS
// =============================================================================

/** Distance from outer to inner frame line (fixed) */
const INNER_OFFSET = 3;

/** Spacing between rivets (fixed) */
const RIVET_SPACING = 15;

/** Corner decoration size - rivets start after this offset from the frame edge */
const CORNER_SIZE = 5.2;

/** Offset from frame edge to rivet center line */
const RIVET_EDGE_OFFSET = 1.7;

// =============================================================================
// TYPES
// =============================================================================

export interface TransformFunctions {
  tx: (x: number) => number;
  ty: (y: number) => number;
  ts: (s: number) => number;
}

/** Simple frame specification: position and size */
export interface FrameSpec {
  /** Left edge X coordinate */
  x: number;
  /** Top edge Y coordinate */
  y: number;
  /** Frame width */
  width: number;
  /** Frame height */
  height: number;
}

/** Internal frame config derived from FrameSpec */
interface FrameConfig {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

function specToConfig(spec: FrameSpec): FrameConfig {
  return {
    left: spec.x,
    right: spec.x + spec.width,
    top: spec.y,
    bottom: spec.y + spec.height,
  };
}

// =============================================================================
// CORNER DEFINITION (relative to corner point)
// =============================================================================

/**
 * All coordinates are RELATIVE to the corner point (0, 0).
 * Positive X points INWARD (toward center), positive Y points INWARD (toward center).
 * This is the TOP-RIGHT corner as reference.
 */

interface RelativeArcBase {
  x: number;
  y: number;
  radius: number;
  largeArc: 0 | 1;
  sweep: 0 | 1;
}

interface RelativeArcAbsolute extends RelativeArcBase {
  endX: number;
  endY: number;
}

interface RelativeArcDelta extends RelativeArcBase {
  dx: number;
  dy: number;
}

type RelativeArc = RelativeArcAbsolute | RelativeArcDelta;

interface RelativeLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface CornerDefinition {
  mainArc: RelativeArc;
  innerArcs: RelativeArc[];
  lines: RelativeLine[];
  ball: { x: number; y: number };
}

/**
 * Base corner definition (TOP-RIGHT corner).
 * All coordinates relative to corner point.
 */
const BASE_CORNER: CornerDefinition = {
  mainArc: {
    x: 3.52036,
    y: 0,
    radius: 2.5,
    largeArc: 1,
    sweep: 0,
    endX: 0,
    endY: -3.48162,
  },
  innerArcs: [
    {
      x: 4.26156,
      y: -4.91866,
      radius: 2.183884,
      largeArc: 0,
      sweep: 0,
      dx: 0.6571,
      dy: 0.6571,
    },
    {
      x: 4.91866,
      y: -4.26156,
      radius: 1.1376598,
      largeArc: 0,
      sweep: 0,
      dx: 0.0667,
      dy: 1.41739,
    },
    {
      x: 4,
      y: -0.2,
      radius: 3.0348086,
      largeArc: 0,
      sweep: 0,
      dx: 0.98533,
      dy: -2.64417,
    },
    {
      x: 2.84417,
      y: -4.98532,
      radius: 1.1376598,
      largeArc: 0,
      sweep: 0,
      dx: 1.41739,
      dy: 0.0667,
    },
    {
      x: 2.84417,
      y: -4.98532,
      radius: 3.0348086,
      largeArc: 0,
      sweep: 0,
      endX: 0.2,
      endY: -4,
    },
    {
      x: 3.24886,
      y: 0,
      radius: 0.1,
      largeArc: 0,
      sweep: 0,
      dx: -0.0779,
      dy: -0.16264,
    },
    {
      x: 0.16364,
      y: -3.13164,
      radius: 0.1,
      largeArc: 0,
      sweep: 0,
      endX: 0,
      endY: -3.20878,
    },
    {
      x: 0.16364,
      y: -3.13164,
      radius: 2.1240317,
      largeArc: 0,
      sweep: 0,
      dx: 3.00727,
      dy: 2.969,
    },
  ],
  lines: [
    { x1: 5.2, y1: -0.2, x2: 4, y2: -0.2 },
    { x1: 0.2, y1: -5.2, x2: 0.2, y2: -4 },
    { x1: 4, y1: 0, x2: 4, y2: -0.2 },
    { x1: 0, y1: -4, x2: 0.2, y2: -4 },
    { x1: 5.2, y1: 0, x2: 5.2, y2: -0.2 },
    { x1: 0, y1: -5.2, x2: 0.2, y2: -5.2 },
    { x1: 3.24886, y1: 0, x2: 5.2, y2: 0 },
    { x1: 0, y1: -3.20878, x2: 0, y2: -5.2 },
  ],
  ball: { x: 3.976, y: -3.976 },
};

// =============================================================================
// CORNER TRANSFORMATION
// =============================================================================

type CornerPosition = "top-right" | "top-left" | "bottom-left" | "bottom-right";

interface CornerTransform {
  flipX: 1 | -1;
  flipY: 1 | -1;
  cornerX: number;
  cornerY: number;
}

function getCornerTransform(
  position: CornerPosition,
  config: FrameConfig,
): CornerTransform {
  switch (position) {
    case "top-right":
      return { flipX: 1, flipY: 1, cornerX: config.right, cornerY: config.top };
    case "top-left":
      return { flipX: -1, flipY: 1, cornerX: config.left, cornerY: config.top };
    case "bottom-left":
      return {
        flipX: -1,
        flipY: -1,
        cornerX: config.left,
        cornerY: config.bottom,
      };
    case "bottom-right":
      return {
        flipX: 1,
        flipY: -1,
        cornerX: config.right,
        cornerY: config.bottom,
      };
  }
}

interface TransformedArcBase {
  x: number;
  y: number;
  radius: number;
  largeArc: 0 | 1;
  sweep: 0 | 1;
}

interface TransformedArcAbsolute extends TransformedArcBase {
  endX: number;
  endY: number;
}

interface TransformedArcDelta extends TransformedArcBase {
  dx: number;
  dy: number;
}

type TransformedArc = TransformedArcAbsolute | TransformedArcDelta;

function transformArc(arc: RelativeArc, ct: CornerTransform): TransformedArc {
  const x = ct.cornerX - arc.x * ct.flipX;
  const y = ct.cornerY - arc.y * ct.flipY;

  // Sweep direction flips when mirroring over ONE axis (not both)
  const shouldFlipSweep = ct.flipX !== ct.flipY;
  const flippedSweep = arc.sweep === 0 ? 1 : 0;
  const sweep = shouldFlipSweep ? flippedSweep : arc.sweep;

  if ("endX" in arc) {
    return {
      x,
      y,
      radius: arc.radius,
      largeArc: arc.largeArc,
      sweep,
      endX: ct.cornerX - arc.endX * ct.flipX,
      endY: ct.cornerY - arc.endY * ct.flipY,
    };
  }
  return {
    x,
    y,
    radius: arc.radius,
    largeArc: arc.largeArc,
    sweep,
    dx: -arc.dx * ct.flipX,
    dy: -arc.dy * ct.flipY,
  };
}

function transformLine(
  line: RelativeLine,
  ct: CornerTransform,
): { x1: number; y1: number; x2: number; y2: number } {
  return {
    x1: ct.cornerX - line.x1 * ct.flipX,
    y1: ct.cornerY - line.y1 * ct.flipY,
    x2: ct.cornerX - line.x2 * ct.flipX,
    y2: ct.cornerY - line.y2 * ct.flipY,
  };
}

function transformBall(
  ball: { x: number; y: number },
  ct: CornerTransform,
): { cx: number; cy: number } {
  return {
    cx: ct.cornerX - ball.x * ct.flipX,
    cy: ct.cornerY - ball.y * ct.flipY,
  };
}

// =============================================================================
// SVG RENDERING
// =============================================================================

function renderArc(
  arc: TransformedArc,
  tf: TransformFunctions,
  sw: number,
): string {
  const { tx, ty, ts } = tf;
  const r = ts(arc.radius);
  const startX = tx(arc.x).toFixed(2);
  const startY = ty(arc.y).toFixed(2);

  if ("endX" in arc) {
    const endX = tx(arc.endX).toFixed(2);
    const endY = ty(arc.endY).toFixed(2);
    return `<path d="M ${startX},${startY} A ${r.toFixed(2)},${r.toFixed(2)} 0 ${arc.largeArc} ${arc.sweep} ${endX},${endY}" fill="none" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`;
  }
  const dx = ts(arc.dx).toFixed(2);
  const dy = ts(-arc.dy).toFixed(2);
  return `<path d="M ${startX},${startY} a ${r.toFixed(2)},${r.toFixed(2)} 0 ${arc.largeArc} ${arc.sweep} ${dx},${dy}" fill="none" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`;
}

function renderLine(
  line: ReturnType<typeof transformLine>,
  tf: TransformFunctions,
  sw: number,
): string {
  const { tx, ty } = tf;
  return `<line x1="${tx(line.x1).toFixed(2)}" y1="${ty(line.y1).toFixed(2)}" x2="${tx(line.x2).toFixed(2)}" y2="${ty(line.y2).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`;
}

function renderCorner(
  position: CornerPosition,
  config: FrameConfig,
  tf: TransformFunctions,
  strokeWidth: number,
): string {
  const ct = getCornerTransform(position, config);
  const sw = tf.ts(strokeWidth);
  const paths: string[] = [];

  paths.push(renderArc(transformArc(BASE_CORNER.mainArc, ct), tf, sw));
  for (const arc of BASE_CORNER.innerArcs) {
    paths.push(renderArc(transformArc(arc, ct), tf, sw));
  }
  for (const line of BASE_CORNER.lines) {
    paths.push(renderLine(transformLine(line, ct), tf, sw));
  }

  return paths.join("\n  ");
}

// =============================================================================
// SYMMETRIC RIVET CALCULATION
// =============================================================================

/**
 * Calculate symmetric rivet positions along an edge.
 * Rivets are centered so the first and last rivet have equal distance to the corners.
 *
 * @param start - Start coordinate (after corner area)
 * @param end - End coordinate (before corner area)
 * @returns Array of rivet positions
 */
function calculateSymmetricRivetPositions(
  start: number,
  end: number,
): number[] {
  const availableLength = end - start;
  if (availableLength < RIVET_SPACING) {
    // Not enough space for rivets
    return availableLength > 0 ? [(start + end) / 2] : [];
  }

  // Calculate how many full spacings fit
  const numSpacings = Math.floor(availableLength / RIVET_SPACING);
  const totalRivetSpan = numSpacings * RIVET_SPACING;

  // Center the rivets: equal margin on both sides
  const margin = (availableLength - totalRivetSpan) / 2;

  const positions: number[] = [];
  for (let i = 0; i <= numSpacings; i++) {
    positions.push(start + margin + i * RIVET_SPACING);
  }

  return positions;
}

// =============================================================================
// PUBLIC API
// =============================================================================

function generateAllCornerDecorations(
  tf: TransformFunctions,
  config: FrameConfig,
  strokeWidth = 0.1,
): string {
  const positions: CornerPosition[] = [
    "top-right",
    "top-left",
    "bottom-left",
    "bottom-right",
  ];
  return positions
    .map((pos) => renderCorner(pos, config, tf, strokeWidth))
    .join("\n  ");
}

function generateCornerBalls(
  tf: TransformFunctions,
  config: FrameConfig,
  radius = 0.4,
  strokeWidth = 0.1,
): string {
  const { tx, ty, ts } = tf;
  const r = ts(radius);
  const sw = ts(strokeWidth);

  const positions: CornerPosition[] = [
    "top-right",
    "top-left",
    "bottom-left",
    "bottom-right",
  ];

  return positions
    .map((pos) => {
      const ct = getCornerTransform(pos, config);
      const ball = transformBall(BASE_CORNER.ball, ct);
      return `<circle cx="${tx(ball.cx).toFixed(2)}" cy="${ty(ball.cy).toFixed(2)}" r="${r.toFixed(2)}" fill="#000" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`;
    })
    .join("\n  ");
}

function generateRivets(
  tf: TransformFunctions,
  config: FrameConfig,
  radius = 0.5,
  strokeWidth = 0.15,
): string {
  const { tx, ty, ts } = tf;
  const r = ts(radius);
  const sw = ts(strokeWidth);
  const rivets: string[] = [];

  // Horizontal rivets (top and bottom edges)
  const horizontalStart = config.left + CORNER_SIZE + RIVET_EDGE_OFFSET;
  const horizontalEnd = config.right - CORNER_SIZE - RIVET_EDGE_OFFSET;
  const horizontalPositions = calculateSymmetricRivetPositions(
    horizontalStart,
    horizontalEnd,
  );

  const topY = config.top + RIVET_EDGE_OFFSET;
  const bottomY = config.bottom - RIVET_EDGE_OFFSET;

  for (const x of horizontalPositions) {
    rivets.push(
      `<circle cx="${tx(x).toFixed(2)}" cy="${ty(topY).toFixed(2)}" r="${r.toFixed(2)}" fill="none" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    );
    rivets.push(
      `<circle cx="${tx(x).toFixed(2)}" cy="${ty(bottomY).toFixed(2)}" r="${r.toFixed(2)}" fill="none" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    );
  }

  // Vertical rivets (left and right edges)
  const verticalStart = config.top + CORNER_SIZE + RIVET_EDGE_OFFSET + 7.8; // Additional offset for corner decoration
  const verticalEnd = config.bottom - CORNER_SIZE - RIVET_EDGE_OFFSET - 7.8;
  const verticalPositions = calculateSymmetricRivetPositions(
    verticalStart,
    verticalEnd,
  );

  const leftX = config.left + RIVET_EDGE_OFFSET;
  const rightX = config.right - RIVET_EDGE_OFFSET;

  for (const y of verticalPositions) {
    rivets.push(
      `<circle cx="${tx(leftX).toFixed(2)}" cy="${ty(y).toFixed(2)}" r="${r.toFixed(2)}" fill="none" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    );
    rivets.push(
      `<circle cx="${tx(rightX).toFixed(2)}" cy="${ty(y).toFixed(2)}" r="${r.toFixed(2)}" fill="none" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    );
  }

  return rivets.join("\n  ");
}

function generateFrameLines(
  tf: TransformFunctions,
  config: FrameConfig,
  strokeWidth = 0.1,
): string {
  const { tx, ty, ts } = tf;
  const sw = ts(strokeWidth);

  // Outer frame coordinates
  const outer = {
    topY: config.top + 0.2,
    bottomY: config.bottom - 0.2,
    leftX: config.left + 0.2,
    rightX: config.right - 0.2,
    hStart: config.left + CORNER_SIZE,
    hEnd: config.right - CORNER_SIZE,
    vStart: config.top + CORNER_SIZE,
    vEnd: config.bottom - CORNER_SIZE,
  };

  // Inner frame coordinates
  const inner = {
    topY: config.top + INNER_OFFSET + 0.2,
    bottomY: config.bottom - INNER_OFFSET - 0.2,
    leftX: config.left + INNER_OFFSET + 0.2,
    rightX: config.right - INNER_OFFSET - 0.2,
    hStart: config.left + CORNER_SIZE - 0.04206,
    hEnd: config.right - CORNER_SIZE + 0.04206,
    vStart: config.top + CORNER_SIZE - 0.04206,
    vEnd: config.bottom - CORNER_SIZE + 0.04206,
  };

  return [
    // Outer frame
    `<line x1="${tx(outer.hStart).toFixed(2)}" y1="${ty(outer.topY).toFixed(2)}" x2="${tx(outer.hEnd).toFixed(2)}" y2="${ty(outer.topY).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    `<line x1="${tx(outer.hStart).toFixed(2)}" y1="${ty(outer.bottomY).toFixed(2)}" x2="${tx(outer.hEnd).toFixed(2)}" y2="${ty(outer.bottomY).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    `<line x1="${tx(outer.leftX).toFixed(2)}" y1="${ty(outer.vStart).toFixed(2)}" x2="${tx(outer.leftX).toFixed(2)}" y2="${ty(outer.vEnd).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    `<line x1="${tx(outer.rightX).toFixed(2)}" y1="${ty(outer.vStart).toFixed(2)}" x2="${tx(outer.rightX).toFixed(2)}" y2="${ty(outer.vEnd).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    // Inner frame
    `<line x1="${tx(inner.hStart).toFixed(2)}" y1="${ty(inner.topY).toFixed(2)}" x2="${tx(inner.hEnd).toFixed(2)}" y2="${ty(inner.topY).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    `<line x1="${tx(inner.hStart).toFixed(2)}" y1="${ty(inner.bottomY).toFixed(2)}" x2="${tx(inner.hEnd).toFixed(2)}" y2="${ty(inner.bottomY).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    `<line x1="${tx(inner.leftX).toFixed(2)}" y1="${ty(inner.vStart).toFixed(2)}" x2="${tx(inner.leftX).toFixed(2)}" y2="${ty(inner.vEnd).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
    `<line x1="${tx(inner.rightX).toFixed(2)}" y1="${ty(inner.vStart).toFixed(2)}" x2="${tx(inner.rightX).toFixed(2)}" y2="${ty(inner.vEnd).toFixed(2)}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>`,
  ].join("\n  ");
}

// =============================================================================
// MAIN ENTRY POINT
// =============================================================================

/**
 * Generate a complete decorative frame with corner decorations, frame lines, and rivets.
 *
 * @param tf - Coordinate transformation functions
 * @param spec - Frame position and size (x, y, width, height)
 * @returns SVG elements as string
 *
 * @example
 * // Create a frame at position (10, 200) with size 180x90
 * generateDecorativeFrame(transform, { x: 10, y: 200, width: 180, height: 90 })
 */
export function generateDecorativeFrame(
  tf: TransformFunctions,
  spec: FrameSpec,
): string {
  const config = specToConfig(spec);

  return [
    generateFrameLines(tf, config),
    generateAllCornerDecorations(tf, config, 0.1),
    generateRivets(tf, config),
    generateCornerBalls(tf, config, 0.4, 0.1),
  ].join("\n  ");
}
